/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import React, { useState } from 'react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';

interface Rule {
  id: string;
  type: 'inList' | 'random';
  identifier: string;
  list?: string[];
  percentage?: number;
  value: boolean;
  featureFlagId: string;
}

export interface FeatureFlag {
  id: string;
  name: string;
  valueType: 'boolean' | 'string' | 'number';
  defaultValue: boolean | string | number;
  rules: Rule[];
}

interface FeatureFlagCardProps {
  featureFlags: FeatureFlag[];
  setCurrentScreen: (screen: string) => void;
  setSelectedFlag: (flag: FeatureFlag) => void;
  onDeleteFlag: (flagName: string) => Promise<boolean>;
  error: string;
}

const FeatureFlagCard: React.FC<FeatureFlagCardProps> = ({
  featureFlags,
  setCurrentScreen,
  setSelectedFlag,
  onDeleteFlag,
  error,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFlag, setSelectedFlagLocal] = useState<FeatureFlag | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const {
    isOpen: isOpenDeleteConfirmation,
    onOpen: onOpenDeleteConfirmation,
    onClose: onCloseDeleteConfirmation,
  } = useDisclosure();

  const toast = useToast();
  const cancelRef = React.useRef();

  const handleRowClick = (flag: FeatureFlag) => {
    setSelectedFlagLocal(flag);
    onOpen();
  };

  const handleEdit = (e: React.MouseEvent, flag: FeatureFlag) => {
    e.stopPropagation();
    setSelectedFlag(flag);
    setCurrentScreen('createNewFlag');
  };

  const handleDelete = (e: React.MouseEvent, flag: FeatureFlag) => {
    e.stopPropagation();
    setSelectedFlagLocal(flag);
    onOpenDeleteConfirmation();
  };

  const handleAlertDialogClose = () => {
    setSelectedFlagLocal(null);
    onCloseDeleteConfirmation();
  };

  const handleDeleteConfirmation = async () => {
    try {
      setIsDeleting(true);
      const isDeleted = await onDeleteFlag(selectedFlag?.name ?? '');
      if (isDeleted) {
        toast({
          title: 'Success',
          description: 'Feature flag deleted successfully',
          status: 'success',
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Something went wrong while deleting feature flag',
          status: 'error',
          position: 'top-right',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong while deleting feature flag',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setIsDeleting(false);
      handleAlertDialogClose();
    }
  };

  const renderFlagValue = (flag: FeatureFlag) => {
    switch (flag.valueType) {
      case 'boolean':
        return <Text>{flag.defaultValue.toString()}</Text>;
      case 'string':
        return <Text>{flag.defaultValue as string}</Text>;
      case 'number':
        return <Text>{flag.defaultValue as number}</Text>;
      default:
        return null;
    }
  };

  if (error) {
    return <Text>{error}</Text>;
  }

  return (
    <>
      <TableContainer border="1px solid #e2e8f0" borderRadius="lg">
        <Table variant="simple" borderBottom="none">
          <Thead>
            <Tr>
              <Th textAlign="center">
                <Heading className="x-small-font">Serial No.</Heading>
              </Th>
              <Th textAlign="center">
                <Heading className="x-small-font">Name</Heading>
              </Th>
              <Th textAlign="center">
                <Heading className="x-small-font">Value Type</Heading>
              </Th>
              <Th textAlign="center">
                <Heading className="x-small-font">Default Value</Heading>
              </Th>
              <Th textAlign="center">
                <Heading className="x-small-font">Edit</Heading>
              </Th>
              <Th textAlign="center">
                <Heading className="x-small-font">Delete</Heading>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {featureFlags?.map((flag, index) => (
              <Tr
                key={flag.id}
                onClick={() => handleRowClick(flag)}
                cursor="pointer"
                _hover={{ backgroundColor: 'gray.50' }}
              >
                <Td textAlign="center">{index + 1}</Td>
                <Td textAlign="center" textTransform="capitalize">
                  {flag.name}
                </Td>
                <Td textAlign="center">{flag.valueType}</Td>
                <Td textAlign="center">
                  <Tag
                    colorScheme={
                      flag.valueType === 'boolean'
                        ? flag.defaultValue
                          ? 'green'
                          : 'red'
                        : 'gray'
                    }
                  >
                    {renderFlagValue(flag)}
                  </Tag>
                </Td>
                <Td textAlign="center">
                  <IconButton
                    size="sm"
                    variant="outline"
                    aria-label="Edit"
                    colorScheme="orange"
                    borderColor="orange"
                    onClick={(e) => handleEdit(e, flag)}
                    _hover={{
                      bg: 'orange.100',
                      color: 'orange.800',
                    }}
                    icon={
                      <EditIcon color="orange.500" _hover={{ color: 'orange.800' }} />
                    }
                  />
                </Td>
                <Td textAlign="center">
                  <IconButton
                    size="sm"
                    variant="outline"
                    aria-label="Delete"
                    colorScheme="red"
                    borderColor="red"
                    onClick={(e) => handleDelete(e, flag)}
                    _hover={{
                      bg: 'red.100',
                      color: 'red.800',
                    }}
                    icon={<DeleteIcon color="red.500" _hover={{ color: 'red.800' }} />}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} size="5xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader textTransform="capitalize">
              {selectedFlag?.name} Rules
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody mb={5}>
              {selectedFlag?.rules.length === 0 ? (
                <Text className="small-font">No rules defined</Text>
              ) : (
                <Flex flexWrap="wrap" justifyContent="center" gap={6} w="100%">
                  {selectedFlag?.rules.map((rule) => (
                    <Flex
                      key={rule.id}
                      direction="column"
                      borderWidth="1px"
                      borderRadius="lg"
                      borderColor="gray.200"
                      overflow="hidden"
                      // width='100%'
                      border="1px solid #e2e8f0"
                      width={{ base: '100%', md: 'calc(50% - 12px)' }}
                      p={2}
                    >
                      {[
                        { label: 'Rule type', value: rule.type },
                        { label: 'Identifier', value: rule.identifier },
                        { label: 'Value', value: rule.value.toString() },
                        {
                          label: rule.type === 'inList' ? 'List' : 'Percentage',
                          value:
                            rule.type === 'inList'
                              ? (rule.list?.join(', ') ?? 'N/A')
                              : rule.percentage !== null
                                ? `${rule.percentage}%`
                                : 'N/A',
                        },
                      ].map(({ label, value }, idx) => (
                        <Flex
                          key={`${rule.id}-${idx}`}
                          width="100%"
                          px={3}
                          py={2}
                          gap={2}
                          alignItems="center"
                          borderBottom={idx === 3 ? 'none' : '1px solid #e2e8f0'}
                        >
                          <Text
                            fontWeight="bold"
                            width="20%"
                            verticalAlign="top"
                            fontFamily="inter"
                            className="small-font"
                          >
                            {label}
                          </Text>
                          <Text
                            width="80%"
                            fontFamily="inter"
                            className="small-font"
                            lineHeight="1.5"
                          >
                            {value}
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  ))}
                </Flex>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
      {isOpenDeleteConfirmation && (
        <AlertDialog
          isOpen={isOpenDeleteConfirmation}
          onClose={handleAlertDialogClose}
          leastDestructiveRef={cancelRef as any}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader
                fontSize="lg"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Delete Feature Flag - {selectedFlag?.name}
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure? You can&apos;t undo this action afterwards.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button
                  ref={cancelRef as unknown as React.LegacyRef<HTMLButtonElement>}
                  onClick={onCloseDeleteConfirmation}
                  isDisabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDeleteConfirmation}
                  ml={3}
                  isLoading={isDeleting}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </>
  );
};

export default FeatureFlagCard;
